/**
 * Name generation utilities for NewME
 * Creates memorable random names similar to Xbox Gamertags or GCP projects
 */

// Adjectives for display names
const ADJECTIVES = [
  "Swift",
  "Brave",
  "Clever",
  "Bright",
  "Calm",
  "Daring",
  "Eager",
  "Friendly",
  "Gentle",
  "Happy",
  "Jolly",
  "Kind",
  "Lively",
  "Merry",
  "Nice",
  "Proud",
  "Quick",
  "Reliable",
  "Smart",
  "Thoughtful",
  "Witty",
  "Zealous",
  "Adventurous",
  "Creative",
  "Dynamic",
  "Energetic",
  "Fantastic",
  "Glorious",
  "Harmonious",
  "Innovative",
  "Joyful",
  "Knowledgeable",
  "Luminous",
  "Magnificent",
  "Optimistic",
  "Peaceful",
  "Radiant",
  "Spectacular",
  "Tremendous",
  "Ultimate",
  "Vibrant",
  "Wonderful",
  "Amazing",
  "Brilliant",
  "Charming",
  "Delightful",
  "Excellent",
  "Fabulous",
  "Gorgeous",
  "Handsome",
  "Incredible",
  "Marvelous",
  "Outstanding",
  // Ancient Iran & Mesopotamia themed adjectives
  "Persian",
  "Babylonian",
  "Assyrian",
  "Sumerian",
  "Achaemenid",
  "Parthian",
  "Sassanian",
  "Elamite",
  "Median",
  "Zoroastrian",
  "Cuneiform",
  "Cylindrical",
  "Hieroglyphic",
  "Mystical",
  "Ancient",
  "Timeless",
  "Imperial",
  "Royal",
  "Noble",
  "Sacred",
  "Divine",
  "Eternal",
  "Golden",
  "Silver",
  "Bronze",
  "Ceramic",
  "Lapis",
  "Turquoise",
  "Amber",
  "Jade",
  "Onyx",
  "Pearl",
  "Crimson",
  "Azure",
  "Verdant",
  "Saffron",
  "Indigo",
  "Violet",
  "Amethyst",
  "Emerald",
  "Ruby",
  "Diamond",
  // Contemporary regional themes
  "Kurdish",
  "Persian",
  "Turkish",
  "Syrian",
  "Afghan",
  "Iraqi",
  "Revolutionary",
  "Liberated",
  "Resilient",
  "Hopeful",
  "Courageous",
  "United",
  "Solidarity",
  "Freedom",
  "Justice",
  "Peaceful",
  "Democratic",
  "Progressive",
  "Empowered",
  "Awakened",
];

// Nouns for display names
const NOUNS = [
  "Explorer",
  "Adventurer",
  "Creator",
  "Builder",
  "Designer",
  "Artist",
  "Scholar",
  "Warrior",
  "Sage",
  "Hero",
  "Champion",
  "Master",
  "Legend",
  "Pioneer",
  "Trailblazer",
  "Navigator",
  "Discoverer",
  "Innovator",
  "Visionary",
  "Dreamer",
  "Thinker",
  "Planner",
  "Organizer",
  "Coordinator",
  "Manager",
  "Leader",
  "Guide",
  "Mentor",
  "Teacher",
  "Student",
  "Researcher",
  "Scientist",
  "Engineer",
  "Architect",
  "Craftsman",
  "Artisan",
  "Specialist",
  "Expert",
  "Professional",
  "Enthusiast",
  "Collector",
  "Curator",
  "Historian",
  "Geographer",
  "Cartographer",
  "Surveyor",
  "Scout",
  "Ranger",
  "Guardian",
  "Protector",
  "Defender",
  "Knight",
  "Wizard",
  "Mage",
  "Sorcerer",
  "Alchemist",
  "Astronomer",
  "Biologist",
  "Chemist",
  // Contemporary regional nouns
  "Revolutionary",
  "Activist",
  "Protester",
  "Organizer",
  "Journalist",
  "Artist",
  "Poet",
  "Writer",
  "Student",
  "Teacher",
  "Doctor",
  "Nurse",
  "Engineer",
  "Lawyer",
  "Humanitarian",
  "Volunteer",
  "Community",
  "Movement",
  "Resistance",
  "Solidarity",
  "Unity",
  "Freedom",
  "Justice",
  "Peace",
  "Democracy",
  "Progress",
  "Hope",
  "Courage",
  "Strength",
  "Resilience",
  "Awakening",
  "Liberation",
  "Empowerment",
  "Change",
  "Future",
  "Dream",
  "Vision",
  "Voice",
  "Story",
  "Truth",
  "Light",
];

// Username-friendly words (shorter, no spaces)
const USERNAME_WORDS = [
  "Alpha",
  "Beta",
  "Gamma",
  "Delta",
  "Echo",
  "Foxtrot",
  "Golf",
  "Hotel",
  "India",
  "Juliet",
  "Kilo",
  "Lima",
  "Mike",
  "November",
  "Oscar",
  "Papa",
  "Quebec",
  "Romeo",
  "Sierra",
  "Tango",
  "Uniform",
  "Victor",
  "Whiskey",
  "Xray",
  "Yankee",
  "Zulu",
  "Ace",
  "Blade",
  "Cobra",
  "Dragon",
  "Eagle",
  "Falcon",
  "Ghost",
  "Hawk",
  "Ion",
  "Jazz",
  "Knight",
  "Laser",
  "Mage",
  "Nova",
  "Orbit",
  "Pulse",
  "Quantum",
  "Raven",
  "Shadow",
  "Thunder",
  "Viper",
  "Wolf",
  "Zen",
  "Bolt",
  "Cipher",
  "Dusk",
  "Echo",
  "Flux",
  "Gleam",
  "Haze",
  "Iris",
  "Jolt",
  "Kite",
  "Luna",
  "Mist",
  "Nebula",
  // Contemporary regional username words
  "Revolution",
  "Freedom",
  "Justice",
  "Peace",
  "Hope",
  "Courage",
  "Strength",
  "Unity",
  "Solidarity",
  "Resistance",
  "Awakening",
  "Liberation",
  "Empowerment",
  "Change",
  "Future",
  "Dream",
  "Vision",
  "Voice",
  "Story",
  "Truth",
  "Light",
  "Dawn",
  "Sunrise",
  "Spring",
  "Rebirth",
  "Renewal",
  "Transformation",
  "Progress",
  "Democracy",
  "Humanity",
  "Dignity",
  "Rights",
  "Equality",
];

// Numbers for variety
const NUMBERS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

/**
 * Generates a random memorable display name
 * Format: "Adjective Noun" (e.g., "Swift Explorer", "Brave Creator")
 * @returns A memorable display name
 */
export const generateDisplayName = (): string => {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  return `${adjective} ${noun}`;
};

/**
 * Generates a random memorable username
 * Format: "Word" or "WordNumber" (e.g., "Alpha", "Dragon7")
 * @returns A memorable username
 */
export const generateUsername = (): string => {
  const word =
    USERNAME_WORDS[Math.floor(Math.random() * USERNAME_WORDS.length)];
  const useNumber = Math.random() < 0.3; // 30% chance to add a number

  if (useNumber) {
    const number = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
    return `${word}${number}`;
  }

  return word;
};

/**
 * Generates a unique temporary username for new Google users
 * Format: "Word" or "WordNumber" (e.g., "Alpha", "Dragon7")
 * @returns A memorable temporary username
 */
export const generateTemporaryUsername = (): string => {
  return generateUsername();
};

/**
 * Generates a unique temporary display name for new Google users
 * Format: "Adjective Noun" (e.g., "Swift Explorer", "Brave Creator")
 * @returns A memorable temporary display name
 */
export const generateTemporaryDisplayName = (): string => {
  return generateDisplayName();
};

/**
 * Generates a set of temporary credentials for new Google users
 * @returns Object with temporary username and display name
 */
export const generateTemporaryCredentials = () => {
  return {
    username: generateTemporaryUsername(),
    displayName: generateTemporaryDisplayName(),
  };
};
